// Shared config & helpers for the Briefs feature

export const ASPECT_RATIOS = ['16:9', '9:16', '1:1', '4:5', '2.39:1'];
export const DELIVERABLE_PLATFORMS = ['Free To Air', 'BVOD', 'SVOD', 'Netflix', 'Social Media'];
export const EDIT_LENGTHS = ['6', '15', '30', '45', '60', '120', '>2mins'];

export function formatEditLength(v) {
  if (!v) return '';
  return v === '>2mins' ? '>2 mins' : `${v}s`;
}

// Production / shoot detail fields. clientFacing=false means internal only (hidden from client form).
export const BRIEF_FIELDS = [
  { key: 'objective', label: 'Objective / Goals', clientFacing: true, placeholder: 'What is this production trying to achieve?' },
  { key: 'overview', label: 'Project Overview', clientFacing: true, placeholder: 'A summary of the production / shoot.' },
  { key: 'target_audience', label: 'Target Audience', clientFacing: true, placeholder: 'Who is this for?' },
  { key: 'key_messages', label: 'Key Messages', clientFacing: true, placeholder: 'Core messages to communicate.' },
  { key: 'locations', label: 'Locations', clientFacing: true, placeholder: 'Where will this be shot?' },
  { key: 'references', label: 'References / Mood', clientFacing: true, placeholder: 'Links or notes on look & feel.' },
  { key: 'talent_crew_notes', label: 'Talent & Crew Notes', clientFacing: true, placeholder: 'Talent, casting, or crew requirements.' },
  { key: 'deadlines', label: 'Deadlines', clientFacing: true, placeholder: 'Key dates and delivery deadlines.' },
  { key: 'brand_guidelines', label: 'Brand Guidelines', clientFacing: true, placeholder: 'Brand rules, fonts, colours, logos.' },
  { key: 'notes', label: 'Internal Notes', clientFacing: false, placeholder: 'Private notes — never shown to the client.' },
];

export const CLIENT_FACING_FIELDS = BRIEF_FIELDS.filter((f) => f.clientFacing);

export const BRIEF_STATUS_META = {
  draft: { label: 'Draft', className: 'border border-dashed border-line-strong bg-transparent text-muted-foreground' },
  sent: { label: 'Sent to client', className: 'bg-mist text-foreground' },
  response_received: { label: 'Response received', className: 'bg-ochre-soft text-ochre' },
  finalised: { label: 'Finalised', className: 'bg-moss-soft text-moss' },
};

export const BRIEF_STATUS_ORDER = ['draft', 'sent', 'response_received', 'finalised'];

export function emptyDeliverable() {
  return {
    id: crypto.randomUUID(),
    name: '',
    aspect_ratios: [],
    resolution: '',
    duration: '',
    platforms: [],
    quantity: 1,
    notes: '',
  };
}

function escapeHtml(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// Render deliverables for the job description, grouped by platform (unbulleted lines).
// Format per line: "{qty}x {length} : {edit name} : {ratio}"
export function deliverablesToHtml(deliverables) {
  const list = (deliverables || []).filter(
    (d) => d && (d.name || (d.aspect_ratios || []).length || (d.platforms || []).length || d.duration)
  );
  if (!list.length) return '';

  const groups = {};
  const order = [];
  list.forEach((d) => {
    const platforms = (d.platforms && d.platforms.length) ? d.platforms : ['Unspecified'];
    const ratios = (d.aspect_ratios && d.aspect_ratios.length) ? d.aspect_ratios : [null];
    const qty = d.quantity && Number(d.quantity) > 0 ? Number(d.quantity) : 1;
    const length = formatEditLength(d.duration);
    const seg1 = length ? `${qty}x ${length}` : `${qty}x`;
    platforms.forEach((pf) => {
      if (!groups[pf]) { groups[pf] = []; order.push(pf); }
      ratios.forEach((r) => {
        groups[pf].push([seg1, d.name, r].filter(Boolean).join(' : '));
      });
    });
  });

  let html = '<h2>Deliverables</h2>';
  order.forEach((pf, idx) => {
    if (idx > 0) html += '<p><br></p>';
    html += `<p><strong>Platform: ${escapeHtml(pf)}</strong></p>`;
    groups[pf].forEach((line) => {
      html += `<p>${escapeHtml(line)}</p>`;
    });
  });
  return html;
}

export function deliverableSummary(d) {
  return [d.name, (d.aspect_ratios || []).join('/'), d.resolution, formatEditLength(d.duration), (d.platforms || []).join(', ')]
    .filter(Boolean)
    .join(' · ');
}
